// apps/office/src/pages/office/ApprovalDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Eye,
  CheckCircle,
  XCircle,
  ArrowLeft,
  FileText,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Loader2,
  Calendar,
  FileCheck,
  Home,
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const QUOTE_STATUS = {
  Draft: 'bg-gray-100 text-gray-700',
  'Pending Approval': 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Sent: 'bg-blue-100 text-blue-700',
  Invoiced: 'bg-purple-100 text-purple-700',
};

const INTERIOR_STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700',
  'Pending Approval': 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Invoiced: 'bg-purple-100 text-purple-700',
};

const INVOICE_FIELD_STATUS = {
  Draft: 'bg-gray-100 text-gray-700',
  'Pending Approval': 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-green-100 text-green-700',
  Sent: 'bg-blue-100 text-blue-700',
  Paid: 'bg-emerald-100 text-emerald-700',
};

const ApprovalDashboard = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [selectedItem, setSelectedItem] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState('approve');
  const [activeTab, setActiveTab] = useState('quotations');
  const [filterStatus, setFilterStatus] = useState('pending');

  const isAdmin = ['admin', 'super_admin', 'manager', 'director'].includes(currentUser?.role);

  /* ---------- Quotations ---------- */
  const {
    data: pendingQuotes = [],
    isLoading: quotesLoading,
    error: quotesError,
    refetch: refetchQuotes,
  } = useQuery({
    queryKey: ['approvals', 'quotations', filterStatus],
    queryFn: async () => {
      try {
        let filter = 'status = "Pending Approval" && isLatest = true';
        if (filterStatus === 'approved') filter = 'status = "Approved" && isLatest = true';
        else if (filterStatus === 'rejected') filter = 'status = "Rejected" && isLatest = true';
        else if (filterStatus === 'all') filter = 'isLatest = true';

        const records = await pb.collection('quotations').getFullList({
          sort: '-created',
          filter,
          expand: 'clientJobId',
        });
        return records;
      } catch (error) {
        console.error('[Approvals] Quotations error:', error);
        toast.error('Failed to load quotations: ' + error.message);
        return [];
      }
    },
    enabled: !!currentUser && isAdmin,
  });

  /* ---------- Invoices ---------- */
  const {
    data: pendingInvoices = [],
    isLoading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['approvals', 'invoices', filterStatus],
    queryFn: async () => {
      try {
        let filter = 'field = "Pending Approval"';
        if (filterStatus === 'approved') filter = 'field = "Approved"';
        else if (filterStatus === 'rejected') filter = 'field = "Draft"';
        else if (filterStatus === 'all') filter = '';

        const records = await pb.collection('invoices').getFullList({
          sort: '-created',
          filter: filter || undefined,
          expand: 'leadId,quotationId',
        });
        return records;
      } catch (error) {
        console.error('[Approvals] Invoices error:', error);
        toast.error('Failed to load invoices: ' + error.message);
        return [];
      }
    },
    enabled: !!currentUser && isAdmin,
  });

  /* ---------- Interior Quotations ---------- */
  const {
    data: pendingInteriorQuotes = [],
    isLoading: interiorLoading,
    refetch: refetchInteriorQuotes,
  } = useQuery({
    queryKey: ['approvals', 'interior-quotations', filterStatus],
    queryFn: async () => {
      try {
        let filter = 'status = "Pending Approval" && isLatest = true';
        if (filterStatus === 'approved') filter = 'status = "Approved" && isLatest = true';
        else if (filterStatus === 'rejected') filter = 'status = "Rejected" && isLatest = true';
        else if (filterStatus === 'all') filter = 'isLatest = true';
        const records = await pb.collection('interior_quotations').getFullList({
          sort: '-created',
          filter,
        });
        return records;
      } catch (error) {
        console.error('[Approvals] Interior error:', error);
        return [];
      }
    },
    enabled: !!currentUser && isAdmin,
  });

  /* ---------- Update Quotation ---------- */
  const updateQuotationStatus = useMutation({
    mutationFn: async ({ id, data }) => {
      const updateData = {
        status: data.status,
        approvalStatus: data.approvalStatus,
        approvedBy: currentUser?.id,
        approvedAt: new Date().toISOString(),
      };
      if (data.notes) updateData.notes = data.notes;
      return await pb.collection('quotations').update(id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals', 'quotations']);
      queryClient.invalidateQueries(['quotations']);
      toast.success(`Quotation ${approvalAction === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowApprovalDialog(false);
      setSelectedItem(null);
      setApprovalNotes('');
      refetchQuotes();
    },
    onError: (error) => toast.error('Failed: ' + (error.message || 'Unknown error')),
  });

  /* ---------- Update Invoice ---------- */
  const updateInvoiceStatus = useMutation({
    mutationFn: async ({ id, field }) => {
      return await pb.collection('invoices').update(id, { field });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals', 'invoices']);
      queryClient.invalidateQueries(['invoices']);
      toast.success('Invoice status updated');
      setShowApprovalDialog(false);
      setSelectedItem(null);
      refetchInvoices();
    },
    onError: (error) => toast.error('Failed: ' + (error.message || 'Unknown error')),
  });

  /* ---------- Update Interior Quotation Status ---------- */
  const updateInteriorStatus = useMutation({
    mutationFn: async ({ id, status, remarks }) => {
      const data = { status };
      if (remarks) data.remarks = remarks;
      if (currentUser?.id) data.approvedBy = currentUser.id;
      data.approvedAt = new Date().toISOString();
      return await pb.collection('interior_quotations').update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals', 'interior-quotations']);
      queryClient.invalidateQueries(['interior-quotations']);
      toast.success('Interior quotation updated successfully');
      setShowApprovalDialog(false);
      setSelectedItem(null);
      setApprovalNotes('');
      refetchInteriorQuotes();
    },
    onError: (error) => {
      console.error('[Approvals] Update interior error:', error);
      toast.error('Failed to update interior quotation: ' + (error.message || 'Unknown'));
    },
  });

  /* ---------- Redirect non-admin ---------- */
  useEffect(() => {
    if (currentUser && !isAdmin) {
      toast.error('You do not have permission to access this page');
      navigate('/office/dashboard');
    }
  }, [currentUser, isAdmin, navigate]);

  if (!currentUser || quotesLoading || invoicesLoading || interiorLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
        <p className="text-slate-500">Loading approval dashboard...</p>
      </div>
    );
  }

  if (!isAdmin) return null;

  /* ---------- Handlers ---------- */
  const handleApproval = (action, type) => {
    if (!selectedItem) return;

    if (type === 'interior') {
      const statusMap = { approve: 'Approved', reject: 'Rejected' };
      updateInteriorStatus.mutate({
        id: selectedItem.id,
        status: statusMap[action] || 'Approved',
        remarks: approvalNotes || '',
      });
      return;
    }

    if (type === 'quotation') {
      const statusMap = { approve: 'Approved', reject: 'Rejected' };
      const approvalMap = { approve: 'approved', reject: 'rejected' };
      updateQuotationStatus.mutate({
        id: selectedItem.id,
        data: {
          status: statusMap[action],
          approvalStatus: approvalMap[action],
          notes: approvalNotes || selectedItem.notes || '',
        },
      });
    } else {
      const fieldMap = { approve: 'Approved', reject: 'Draft' };
      updateInvoiceStatus.mutate({
        id: selectedItem.id,
        field: fieldMap[action],
      });
    }
  };

  const getQuoteStatusBadge = (status) => {
    const cls = QUOTE_STATUS[status] || QUOTE_STATUS.Draft;
    return <Badge className={cls}>{status || 'Draft'}</Badge>;
  };

  const getInvoiceFieldBadge = (field) => {
    const cls = INVOICE_FIELD_STATUS[field] || INVOICE_FIELD_STATUS.Draft;
    return <Badge className={cls}>{field || 'Draft'}</Badge>;
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(price || 0);

  const formatDate = (date) => (date ? format(new Date(date), 'dd MMM yyyy') : '�');

  const getQuoteClientName = (item) =>
    item.clientName || item.expand?.clientJobId?.name || item.expand?.clientJobId?.email || 'Unknown';
  const getQuoteClientContact = (item) =>
    item.clientPhone || item.expand?.clientJobId?.phone || item.clientEmail || '';

  const getInvoiceClientName = (item) =>
    item.expand?.leadId?.name ||
    item.expand?.quotationId?.clientName ||
    'Unknown';
  const getInvoiceClientContact = (item) =>
    item.expand?.leadId?.phone ||
    item.expand?.quotationId?.clientPhone ||
    '';

  const getInvoiceQuotationNumber = (item) =>
    item.expand?.quotationId?.quoteNumber || '�';

  const interiorBadge = (s) => (
    <Badge className={INTERIOR_STATUS_COLORS[s] || INTERIOR_STATUS_COLORS.Draft}>
      {s || 'Draft'}
    </Badge>
  );

  /* ---------- Quotation table ---------- */
  const renderQuotationTable = () => {
    if (pendingQuotes.length === 0) {
      return (
        <div className="text-center py-12">
          <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No pending quotations</p>
          <p className="text-sm text-slate-400 mt-1">All quotations have been reviewed</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Quote #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingQuotes.map((item) => {
              const status = item.status || 'Pending Approval';
              const isPending = status === 'Pending Approval';
              return (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono font-medium">{item.quoteNumber || '�'}</TableCell>
                  <TableCell>
                    <p className="font-medium">{getQuoteClientName(item)}</p>
                    {getQuoteClientContact(item) && (
                      <p className="text-xs text-slate-500">{getQuoteClientContact(item)}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {formatDate(item.created)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-indigo-600">
                    {formatPrice(item.total || 0)}
                  </TableCell>
                  <TableCell>{getQuoteStatusBadge(status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/office/quotation/${item.id}`)} className="h-8 w-8 p-0" title="View">
                        <Eye className="w-4 h-4 text-slate-400" />
                      </Button>
                      {isPending && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setSelectedItem(item); setApprovalAction('approve'); setApprovalNotes(''); setShowApprovalDialog(true); }}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setSelectedItem(item); setApprovalAction('reject'); setApprovalNotes(''); setShowApprovalDialog(true); }}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  /* ---------- Invoice table ---------- */
  const renderInvoiceTable = () => {
    if (pendingInvoices.length === 0) {
      return (
        <div className="text-center py-12">
          <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No pending invoices</p>
          <p className="text-sm text-slate-400 mt-1">
            Send an invoice for approval from the Invoices page
          </p>
          <Button onClick={() => navigate('/office/invoices')} variant="outline" className="mt-4">
            Go to Invoices
          </Button>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>From Quotation</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingInvoices.map((item) => {
              const field = item.field || 'Draft';
              const isPending = field === 'Pending Approval';
              return (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono font-medium">{item.invoiceNumber || '�'}</TableCell>
                  <TableCell className="font-mono text-xs text-indigo-600">
                    {getInvoiceQuotationNumber(item)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{getInvoiceClientName(item)}</p>
                    {getInvoiceClientContact(item) && (
                      <p className="text-xs text-slate-500">{getInvoiceClientContact(item)}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {formatDate(item.invoiceDate || item.created)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-indigo-600">
                    {formatPrice(item.totalAmount || 0)}
                  </TableCell>
                  <TableCell>{getInvoiceFieldBadge(field)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/office/invoices/${item.id}`)} className="h-8 w-8 p-0" title="View">
                        <Eye className="w-4 h-4 text-slate-400" />
                      </Button>
                      {isPending && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setSelectedItem(item); setApprovalAction('approve'); setApprovalNotes(''); setShowApprovalDialog(true); }}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setSelectedItem(item); setApprovalAction('reject'); setApprovalNotes(''); setShowApprovalDialog(true); }}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Send back to Draft"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const totalPending = pendingQuotes.length + pendingInvoices.length + pendingInteriorQuotes.length;

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/office/dashboard')} className="shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileCheck className="w-6 h-6 text-indigo-600" />
              Approval Dashboard
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Review and manage pending quotations and invoices
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px] h-9">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { refetchQuotes(); refetchInvoices(); refetchInteriorQuotes(); }}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Pending Quotations</p><p className="text-xl font-bold text-yellow-600">{pendingQuotes.length}</p></CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Pending Invoices</p><p className="text-xl font-bold text-yellow-600">{pendingInvoices.length}</p></CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Total Pending</p><p className="text-xl font-bold text-slate-800">{totalPending}</p></CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Review Status</p><p className="text-xl font-bold text-indigo-600">{totalPending > 0 ? 'Action Required' : 'All Clear'}</p></CardContent></Card>
      </div>

      {/* Error banners */}
      {(quotesError || invoicesError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="text-xs text-red-700">
            {quotesError && <p>Quotations: {quotesError.message}</p>}
            {invoicesError && <p>Invoices: {invoicesError.message}</p>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="quotations" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg gap-2">
            <FileText className="w-4 h-4" /> Quotations
            {pendingQuotes.length > 0 && (
              <Badge variant="destructive" className="ml-1 px-2 py-0 text-xs">{pendingQuotes.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg gap-2">
            <TrendingUp className="w-4 h-4" /> Invoices
            {pendingInvoices.length > 0 && (
              <Badge variant="destructive" className="ml-1 px-2 py-0 text-xs">{pendingInvoices.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="interior" className="data-[state=active]:bg-white rounded-lg gap-2">
            <Home className="w-4 h-4" /> Interior
            {pendingInteriorQuotes.length > 0 && <Badge variant="destructive" className="ml-1 px-2 py-0 text-xs">{pendingInteriorQuotes.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quotations" className="mt-0">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-500" />
                {filterStatus === 'pending' ? 'Pending' : filterStatus === 'approved' ? 'Approved' : filterStatus === 'rejected' ? 'Rejected' : 'All'} Quotations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">{renderQuotationTable()}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-0">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-500" />
                {filterStatus === 'pending' ? 'Pending' : filterStatus === 'approved' ? 'Approved' : 'All'} Invoices
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">{renderInvoiceTable()}</CardContent>
          </Card>
        </TabsContent>

        {/* Interior Quotations Tab */}
        <TabsContent value="interior" className="mt-4">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Home className="w-4 h-4 text-emerald-500" />
                Pending Interior Quotations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingInteriorQuotes.length === 0 ? (
                <div className="text-center py-12">
                  <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No pending interior quotations</p>
                </div>
              ) : (
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Quote #</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Style</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingInteriorQuotes.map((item) => {
                      const status = item.status || 'Pending Approval';
                      const isPending = status === 'Pending Approval';
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-mono font-medium">{item.quoteNumber}</TableCell>
                          <TableCell>
                            <Badge className="bg-slate-100 text-slate-700 text-xs capitalize">
                              {item.categoryType || '�'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              (item.mainCategory || '').toLowerCase() === 'luxury'
                                ? 'bg-amber-100 text-amber-700 text-xs'
                                : 'bg-blue-100 text-blue-700 text-xs'
                            }>
                              {item.mainCategory || 'Standard'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700">
                            {formatPrice(item.total || item.subtotal || 0)}
                          </TableCell>
                          <TableCell>{formatDate(item.created)}</TableCell>
                          <TableCell>{interiorBadge(status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => navigate(`/office/interior-quotations/view/${item.id}`)}
                                className="h-8 w-8 p-0"
                                title="View"
                              >
                                <Eye className="w-4 h-4 text-slate-400" />
                              </Button>
                              {isPending && (
                                <>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => {
                                      setSelectedItem(item);
                                      setApprovalAction('approve');
                                      setApprovalNotes('');
                                      setShowApprovalDialog(true);
                                    }}
                                    className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                                    title="Approve"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => {
                                      setSelectedItem(item);
                                      setApprovalAction('reject');
                                      setApprovalNotes('');
                                      setShowApprovalDialog(true);
                                    }}
                                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                    title="Reject"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalAction === 'approve' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {approvalAction === 'approve' ? 'Approve' : 'Reject'}{' '}
              {selectedItem?.quoteNumber || selectedItem?.invoiceNumber}
            </DialogTitle>
            <DialogDescription>
              {approvalAction === 'approve'
                ? 'Approve this document.'
                : activeTab === 'invoices'
                  ? 'Send this invoice back to Draft.'
                  : 'Reject this document.'}
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-medium">
                    {activeTab === 'quotations'
                      ? getQuoteClientName(selectedItem)
                      : activeTab === 'invoices'
                        ? getInvoiceClientName(selectedItem)
                        : selectedItem.quoteNumber || '�'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Amount</p>
                  <p className="font-bold text-indigo-600">
                    {activeTab === 'quotations'
                      ? formatPrice(selectedItem.total || 0)
                      : activeTab === 'invoices'
                        ? formatPrice(selectedItem.totalAmount || 0)
                        : formatPrice(selectedItem.total || selectedItem.subtotal || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="text-sm">{formatDate(selectedItem.created)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  {activeTab === 'quotations'
                    ? getQuoteStatusBadge(selectedItem.status || 'Pending Approval')
                    : activeTab === 'invoices'
                      ? getInvoiceFieldBadge(selectedItem.field || 'Draft')
                      : interiorBadge(selectedItem.status || 'Pending Approval')}
                </div>
              </div>

              {activeTab !== 'invoices' && (
                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    rows={3}
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder={approvalAction === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={() => handleApproval(
                approvalAction,
                activeTab === 'quotations' ? 'quotation'
                  : activeTab === 'interior' ? 'interior'
                  : 'invoice'
              )}
              disabled={updateQuotationStatus.isPending || updateInvoiceStatus.isPending || updateInteriorStatus.isPending}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {(updateQuotationStatus.isPending || updateInvoiceStatus.isPending || updateInteriorStatus.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {approvalAction === 'approve' ? 'Approve' : (activeTab === 'invoices' ? 'Send back to Draft' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-xs text-slate-400 text-center border-t border-slate-200 pt-4">
        Approval actions are logged with timestamp and approver information
        <span className="block mt-1">Last updated: {format(new Date(), 'dd MMM yyyy, HH:mm')}</span>
      </p>
    </div>
  );
};

export default ApprovalDashboard;